import { cx } from "../../lib/cx.js";
import { tabbables } from "../_overlay/focus.js";
import { Layer } from "../_overlay/Layer.js";
import { useAnchoredPosition } from "../_overlay/useAnchoredPosition.js";
/* empty css            */
import { cloneElement, useCallback, useId, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Popover/Popover.tsx
function refOf(element) {
	if ("ref" in element.props) return element.props.ref;
	return element.ref;
}
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
/** The trigger with Popover's props merged in (a component, so its handlers are JSX props). */
function Trigger({ element, ...props }) {
	return cloneElement(element, props);
}
/**
* A panel anchored to a trigger: non-modal, closed by the trigger, Escape or a press
* outside. Tab from the trigger moves into it; tabbing past its end moves on to what
* follows the trigger and closes it.
*/
function Popover({ trigger, children, placement = "bottom-start", width, unstyled = false, open: openProp, defaultOpen = false, onOpenChange, "aria-label": ariaLabel, className }) {
	const [openState, setOpenState] = useState(defaultOpen);
	const open = openProp ?? openState;
	const setOpen = useCallback((next) => {
		if (openProp === void 0) setOpenState(next);
		onOpenChange?.(next);
	}, [openProp, onOpenChange]);
	const close = useCallback(() => setOpen(false), [setOpen]);
	const id = useId();
	const triggerId = trigger.props.id ?? `${id}-trigger`;
	const panelId = `${id}-panel`;
	const triggerRef = useRef(null);
	const panelRef = useRef(null);
	const [side, align = "center"] = placement.split("-");
	const position = useAnchoredPosition({
		open,
		side,
		align
	});
	const ownRef = refOf(trigger);
	const setTrigger = useCallback((element) => {
		triggerRef.current = element;
		position.setAnchor(element);
		setRef(ownRef, element);
	}, [position, ownRef]);
	const setPanel = useCallback((element) => {
		panelRef.current = element;
		position.setFloating(element);
	}, [position]);
	const handleTriggerKeyDown = (event) => {
		trigger.props.onKeyDown?.(event);
		if (event.defaultPrevented || !open || event.key !== "Tab" || event.shiftKey) return;
		const first = panelRef.current && tabbables(panelRef.current)[0];
		if (!first) return;
		event.preventDefault();
		first.focus();
	};
	const handlePanelKeyDown = (event) => {
		if (event.key !== "Tab" || !panelRef.current || !triggerRef.current) return;
		const items = tabbables(panelRef.current);
		const active = document.activeElement;
		if (event.shiftKey && (items.length === 0 || active === items[0])) {
			event.preventDefault();
			triggerRef.current.focus();
		} else if (!event.shiftKey && (items.length === 0 || active === items.at(-1))) {
			const panel = panelRef.current;
			const page = tabbables(document.body).filter((element) => !panel.contains(element));
			const next = page[page.indexOf(triggerRef.current) + 1];
			event.preventDefault();
			(next ?? triggerRef.current).focus();
			close();
		}
	};
	const widthStyle = width === void 0 ? void 0 : { inlineSize: typeof width === "number" ? `${width}px` : width };
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Trigger, {
		element: trigger,
		ref: setTrigger,
		onClick: (event) => {
			trigger.props.onClick?.(event);
			if (!event.defaultPrevented) setOpen(!open);
		},
		onKeyDown: handleTriggerKeyDown,
		"aria-haspopup": "dialog",
		"aria-expanded": open,
		"aria-controls": open ? panelId : void 0,
		id: triggerId
	}), open && /* @__PURE__ */ jsx(Layer, {
		kind: "popover",
		ref: setPanel,
		id: panelId,
		role: "dialog",
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabel ? void 0 : triggerId,
		insideRefs: [triggerRef],
		onDismiss: close,
		onKeyDown: handlePanelKeyDown,
		className: cx("ds-popover", !unstyled && "ds-popover--surface", className),
		"data-side": position.side,
		style: {
			...position.style,
			...widthStyle,
			visibility: position.isPositioned ? void 0 : "hidden"
		},
		children: typeof children === "function" ? children({ close }) : children
	})] });
}
//#endregion
export { Popover };

//# sourceMappingURL=Popover.js.map